import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl, formatApiError } from "@/lib/api";
import { PageHead, RangePicker, Empty, monthStartIso, todayIso, addDaysIso } from "@/components/erp/ErpUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { printPdfBlob, preopenForIOS } from "@/lib/print";
import { Users, Download, Loader2, Printer, ExternalLink, TrendingUp, AlertTriangle } from "lucide-react";

const pct = (v) => (v == null ? "—" : `${String(v).replace(".", ",")}%`);
const COLS = [
  ["parts", "TOT. PROD.", "Total de peças (bruto)", brl], ["labor", "TOT. SERV.", "Total de serviços (bruto)", brl], ["cost_total", "V.C.T.", "Valor de custo total", brl],
  ["discount", "DESC.", "Desconto total (itens + total do orçamento)", brl], ["revenue", "V.V.T.", "Valor de venda total (líquido)", brl],
  ["profit", "L.B. R$", "Lucro bruto", brl], ["margin", "L.B. %", "Lucro bruto %", pct], ["markup", "MARKUP %", "Markup", pct],
  ["comm_parts", "VL. COMISS. PRODUTOS", "Comissão peças", brl], ["comm_labor", "VL. COMISS. SERVIÇOS", "Comissão serviços", brl],
  ["commission", "VL. COMISS. TOTAL", "Comissão total", brl], ["orders", "Nº OS", "Quantidade de OS", String],
  ["ticket", "TICKET", "Ticket médio", brl], ["third_party", "TERC.", "Custo terceiros", brl],
  ["profit_per_hour", "L.B./H", "Lucro por hora", (v) => (v == null ? "—" : brl(v))],
  ["hours_real", "H. REAIS", "Horas trabalhadas (cronômetro)", (v) => (v == null ? "—" : `${String(v).replace(".", ",")} h`)],
  ["goal", "META", "Meta mensal", (v) => (v ? brl(v) : "—")], ["goal_pct", "% META", "% da meta atingida", pct],
  ["rework", "RETRAB.", "Retrabalhos", String], ["rating", "NOTA", "Satisfação", (v) => (v == null ? "—" : `${v}★`)],
];
const SIMPLE_COLS = [
  ["cost_total", "V.C.T.", "Custo total", brl],
  ["discount", "DESC.", "Desconto", brl],
  ["revenue", "V.V.T.", "Venda total líquida", brl],
  ["profit", "L.B. R$", "Lucro bruto", brl],
  ["margin", "L.B. %", "Lucro bruto %", pct],
];
const PRESETS = [["Hoje", () => [todayIso(), todayIso()]], ["Semana", () => [addDaysIso(-6), todayIso()]], ["Mês", () => [monthStartIso(), todayIso()]]];
const TABS = [["mechanics", "Mecânico"], ["consultants", "Consultor (atendente)"], ["all", "Equipe toda"]];
const SCOPES = [["all", "OS total"], ["delivered", "OS entregues"]];
const ROLE_PILL = { mecanico: "bg-sky-900/50 text-sky-200 border-sky-800", consultor: "bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-800" };

function Totalizer({ rows, summary, isAll, showRole, consultants, cols, mode }) {
  const t = useMemo(() => {
    const z = { parts: 0, labor: 0, cost_total: 0, discount: 0, revenue: 0, profit: 0, comm_parts: 0, comm_labor: 0, commission: 0, orders: 0 };
    rows.forEach((r) => { Object.keys(z).forEach((k) => { z[k] += Number(r[k] || 0); }); });
    z.margin = z.revenue ? +(z.profit / z.revenue * 100).toFixed(1) : 0;
    z.markup = z.cost_total ? +(((z.revenue - z.cost_total) / z.cost_total) * 100).toFixed(1) : null;
    if (isAll && summary) z.orders = summary.orders;
    if (isAll && consultants) z.discount = (consultants || []).reduce((s, r) => s + Number(r.discount || 0), 0);
    return z;
  }, [rows, isAll, summary, consultants]);
  const fmt = { parts: brl, labor: brl, cost_total: brl, discount: brl, revenue: brl, profit: brl, margin: pct, markup: pct, comm_parts: brl, comm_labor: brl, commission: brl, orders: String };
  const BLANK_IN_COMPLETE = new Set(["parts", "labor"]);
  return (
    <tr data-testid="team-totalizers" className="border-t-2 border-green-neon bg-zinc-900 font-bold">
      <td className="px-3 py-2 text-zinc-100 font-mono" colSpan={showRole ? 3 : 2}>TOTALIZADORES</td>
      {cols.map(([k]) => (
        <td key={k} data-testid={`team-total-${k}`} className={`px-2 py-2 text-right whitespace-nowrap ${k === "discount" ? "text-red-300" : k === "profit" ? (t.profit < 0 ? "text-red-300" : "text-green-neon") : "text-zinc-100"}`}>
          {mode === "complete" && BLANK_IN_COMPLETE.has(k) ? "" : (k in fmt ? fmt[k](t[k]) : "—")}
        </td>
      ))}
      <td />
    </tr>
  );
}

function SalesRanking({ rows, testid }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)), [rows]);
  const max = Math.max(1, ...sorted.map((r) => r.revenue || 0));
  const PALETTE = ["#059669", "#2563EB", "#DC2626", "#F59E0B", "#7C3AED", "#DB2777", "#0891B2", "#CA8A04", "#4F46E5", "#16A34A", "#EA580C", "#9333EA", "#0EA5E9", "#65A30D"];
  if (!sorted.length) return null;
  const chartH = 240;
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-mono-caps uppercase tracking-widest text-green-neon"><TrendingUp className="h-4 w-4" />Ranking de Vendas</div>
      <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
        <div className="flex-1 min-w-[320px] overflow-x-auto">
          <div className="flex items-end gap-3 border-b border-zinc-800 pt-6 pb-2" style={{ minHeight: chartH }}>
            {sorted.map((r, i) => {
              const h = max ? Math.max(4, (r.revenue || 0) / max * (chartH - 50)) : 4;
              return (
                <div key={`${r.role || "x"}-${r.name}`} data-testid={`${testid}-bar`} className="flex flex-col items-center justify-end min-w-[44px]" style={{ height: chartH - 20 }}>
                  <div className="text-[10px] font-bold text-zinc-100 whitespace-nowrap mb-1">{brl(r.revenue || 0)}</div>
                  <div className="w-9 rounded-t-md border border-zinc-900" style={{ height: h, backgroundColor: PALETTE[i % PALETTE.length] }} />
                </div>
              );
            })}
          </div>
          <div className="flex items-start gap-3 pt-1">
            {sorted.map((r) => (
              <div key={`code-${r.role || "x"}-${r.name}`} className="min-w-[44px] text-center text-[10px] font-mono text-zinc-400">{r.code || "—"}</div>
            ))}
          </div>
        </div>
        <div className="shrink-0 w-full md:w-auto md:max-w-[260px] space-y-1" data-testid={`${testid}-legend`}>
          <div className="text-[10px] font-mono-caps uppercase tracking-widest text-zinc-500">Legenda</div>
          {sorted.map((r, i) => (
            <div key={`lg-${r.role || "x"}-${r.name}`} className="flex items-center gap-2 text-xs text-zinc-300">
              <span className="inline-block w-3 h-3 rounded-sm border border-zinc-700" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
              <span className="text-zinc-500 font-mono text-[10px]">{r.code || "—"}</span>
              <span className="truncate">{r.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Table({ rows, sort, setSort, testid, onPrint, showRole, expanded, setExpanded, summary, tab, consultants, mode }) {
  const cols = mode === "simplified" ? SIMPLE_COLS : COLS;
  const effShowRole = showRole && mode !== "simplified";
  const sorted = useMemo(() => [...rows].sort((a, b) => (b[sort] ?? -1e12) - (a[sort] ?? -1e12)), [rows, sort]);
  if (!rows.length) return <Empty>Nenhuma OS no período.</Empty>;
  const minW = mode === "simplified" ? "min-w-[640px]" : (effShowRole ? "min-w-[1320px]" : "min-w-[1200px]");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-zinc-800 overflow-x-auto">
        <table className={`w-full text-sm ${minW}`} data-testid={testid}>
          <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
            <th className="text-left px-3 py-2">CÓD.</th><th className="text-left px-3 py-2">NOME</th>
            {effShowRole && <th className="text-left px-3 py-2">FUNÇÃO</th>}
            {cols.map(([k, l]) => <th key={k} className="text-right px-2 py-2 align-bottom"><button type="button" data-testid={`${testid}-sort-${k}`} onClick={() => setSort(k)} className={`text-right leading-tight ${sort === k ? "text-green-neon" : ""}`}><span className="block whitespace-nowrap">{l}</span></button></th>)}<th className="px-2 py-2" />
          </tr></thead>
          <tbody>{sorted.map((r, i) => {
            const key = `${r.role || "x"}-${r.name}`;
            const isUnassigned = r.name === "Não informado";
            return (
              <Fragment key={key}>
                <tr className={`border-t border-zinc-800 ${isUnassigned ? "bg-amber-950/10" : ""}`}>
                  <td className="px-3 py-2 text-zinc-500 font-mono text-xs">{r.code}</td>
                  <td className="px-3 py-2 text-zinc-100 whitespace-nowrap">
                    {isUnassigned ? (
                      <button type="button" data-testid={`${testid}-unassigned-${i}`} onClick={() => setExpanded(expanded === key ? null : key)} className="inline-flex items-center gap-1 text-amber-200 hover:text-amber-100 underline-offset-2 hover:underline">
                        {r.name} <ExternalLink className="h-3 w-3" />
                      </button>
                    ) : r.name}
                  </td>
                  {effShowRole && <td className="px-3 py-2 whitespace-nowrap"><span className={`inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase border ${ROLE_PILL[r.role] || ""}`}>{r.role_label}</span></td>}
                  {cols.map(([k, , , fmt]) => <td key={k} className={`px-2 py-2 text-right whitespace-nowrap ${k === "discount" ? "text-red-300 font-bold" : k === "profit" ? (r.profit < 0 ? "text-red-300 font-bold" : "text-green-neon font-bold") : "text-zinc-300"}`}>{fmt(r[k])}</td>)}
                  <td className="px-2 py-2"><button type="button" title="Imprimir folha deste funcionário" data-testid={`${testid}-print-${i}`} onClick={() => onPrint(r.name, r.role)} className="text-zinc-400 hover:text-green-neon"><Printer className="h-4 w-4" /></button></td>
                </tr>
                {isUnassigned && expanded === key && (
                  <tr className="bg-amber-950/5 border-t border-amber-900/30">
                    <td colSpan={(effShowRole ? 3 : 2) + cols.length + 1} className="px-3 py-3 text-xs text-amber-100">
                      <div className="mb-2 text-amber-200 font-bold uppercase font-mono-caps tracking-widest">OS sem {r.role === "consultor" ? "consultor" : "mecânico"} atribuído:</div>
                      <ul data-testid={`${testid}-unassigned-list-${i}`} className="space-y-1">
                        {(r.order_list || []).map((o) => (
                          <li key={o.id || o.number} className="flex items-center justify-between gap-2 border-t border-amber-900/30 py-1">
                            <span>OS #{o.number} · {o.date?.split("-").reverse().join("/")} · {o.plate || "sem placa"} · <strong>{brl(o.value)}</strong></span>
                            {o.id && <Link to={`/owner/ordens/${o.id}`} className="inline-flex items-center gap-1 h-7 px-2 rounded-md bg-amber-800/40 hover:bg-amber-800/70 text-amber-100 text-[11px] font-bold uppercase"><ExternalLink className="h-3 w-3" /> Ver OS</Link>}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          <Totalizer rows={rows} summary={summary} isAll={tab === "all"} showRole={effShowRole} consultants={consultants} cols={cols} mode={mode} /></tbody>
        </table>
      </div>
    </div>
  );
}

export default function OwnerTeam() {
  const [range, setRange] = useState(PRESETS[2][1]());
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("mechanics");
  const [scope, setScope] = useState("all");
  const [mode, setMode] = useState("complete");
  const [sort, setSort] = useState("revenue");
  const [printing, setPrinting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  useEffect(() => { setData(null); api.get("/team/report", { params: { from: range[0], to: range[1], scope } }).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, [range, scope]);
  const print = async (name) => {
    const win = preopenForIOS();
    setPrinting(true);
    try {
      const resp = await api.get("/team/report/pdf", { params: { group: tab, from: range[0], to: range[1], scope, mode, ...(name != null ? { name } : {}) }, responseType: "blob" });
      printPdfBlob(resp.data, win);
    } catch (e) { if (win) win.close(); toast.error(formatApiError(e)); } finally { setPrinting(false); }
  };
  const csv = () => {
    const rows = data[tab];
    const extra = tab === "all" && mode !== "simplified" ? ["Função"] : [];
    const cols = mode === "simplified" ? SIMPLE_COLS : COLS;
    const head = ["Código", mode === "simplified" ? "Vendedor" : "Nome", ...extra, ...cols.map((c) => c[1])].join(";");
    const body = rows.map((r) => [r.code, r.name, ...(extra.length ? [r.role_label] : []), ...cols.map(([k]) => (r[k] == null ? "" : String(r[k]).replace(".", ",")))].join(";")).join("\n");
    const z = { parts: 0, labor: 0, cost_total: 0, discount: 0, revenue: 0, profit: 0, comm_parts: 0, comm_labor: 0, commission: 0, orders: 0 };
    rows.forEach((r) => { Object.keys(z).forEach((k) => { z[k] += Number(r[k] || 0); }); });
    z.margin = z.revenue ? +(z.profit / z.revenue * 100).toFixed(1) : 0;
    if (tab === "all" && data?.orders_summary) z.orders = data.orders_summary.orders;
    if (tab === "all" && data?.consultants) z.discount = data.consultants.reduce((s, r) => s + Number(r.discount || 0), 0);
    const BLANK = mode === "complete" ? new Set(["parts", "labor"]) : new Set();
    const totLine = ["TOTAIS", "", ...(extra.length ? [""] : []), ...cols.map(([k]) => (BLANK.has(k) ? "" : (k in z ? String(z[k]).replace(".", ",") : "")))].join(";");
    const url = URL.createObjectURL(new Blob(["\ufeff" + head + "\n" + body + "\n" + totLine], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = `equipe-${tab}-${mode}-${scope}-${range[0]}_${range[1]}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const rows = data?.[tab] || [];
  const sumRow = useMemo(() => rows.reduce((s, r) => s + (r.revenue || 0), 0), [rows]);
  const summary = data?.orders_summary;
  const unassignedConsultants = useMemo(() => (data?.consultants || []).find((r) => r.name === "Não informado"), [data]);
  const openUnassignedConsultants = () => {
    setTab("consultants"); setExpanded("x-Não informado");
    setTimeout(() => {
      const el = document.querySelector('[data-testid="team-consultants-unassigned-0"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
  };
  return (
    <div className="space-y-5 animate-fade-up" data-testid="team-page">
      <PageHead icon={Users} kicker="Análise" title="Desempenho da equipe"><RangePicker from={range[0]} to={range[1]} onChange={setRange} testid="team-range" /></PageHead>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map(([l, fn]) => <Button key={l} size="sm" variant="outline" data-testid={`team-preset-${l}`} onClick={() => setRange(fn())} className="bg-zinc-900 border-zinc-800">{l}</Button>)}
        <span className="mx-2" />
        <span className="self-center text-xs text-zinc-400">Agrupar por:</span>
        {TABS.map(([k, l]) => <Button key={k} size="sm" data-testid={`team-tab-${k}`} onClick={() => { setTab(k); setExpanded(null); }} className={tab === k ? "bg-green-brand text-white" : "bg-zinc-900 text-zinc-300"}>{l}</Button>)}
        <span className="mx-2" />
        <span className="self-center text-xs text-zinc-400">Base:</span>
        {SCOPES.map(([k, l]) => <Button key={k} size="sm" data-testid={`team-scope-${k}`} onClick={() => setScope(k)} className={scope === k ? "bg-amber-brand text-zinc-950" : "bg-zinc-900 text-zinc-300"}>{l}</Button>)}
        <span className="self-center text-xs text-zinc-400">Modo:</span>
        {[["simplified", "Simplificado"], ["complete", "Completo"]].map(([k, l]) => <Button key={k} size="sm" data-testid={`team-mode-${k}`} onClick={() => setMode(k)} className={mode === k ? "bg-sky-600 text-white" : "bg-zinc-900 text-zinc-300"}>{l}</Button>)}
        <div className="ml-auto flex gap-2">
          <Button size="sm" data-testid="team-print" disabled={!data || !rows.length || printing} onClick={() => print(null)} className="bg-green-brand text-white">{printing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />} Imprimir / PDF</Button>
          <Button size="sm" variant="outline" data-testid="team-csv" disabled={!data} onClick={csv} className="bg-zinc-900 border-zinc-800"><Download className="h-4 w-4 mr-1" /> CSV</Button>
        </div>
      </div>
      {summary && (
        <div data-testid="team-highlights" className="rounded-2xl border border-green-brand/40 bg-emerald-950/30 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["orders", "Nº OS", summary.orders, String],
            ["gross_profit_pct", "L.B. %", summary.gross_profit_pct, pct],
            ["ticket", "T.M.V. (ticket médio)", summary.ticket, brl],
            ["cost_total", "Custo total", summary.cost_total, brl],
            ["revenue", "Total de vendas", summary.revenue, brl]].map(([k, lbl, v, fmt]) => (
            <div key={k} data-testid={`team-highlight-${k}`} className="text-center">
              <div className="text-[10px] font-mono-caps uppercase tracking-widest text-emerald-200">{lbl}</div>
              <div className="font-display text-2xl font-black text-white tabular-nums">{fmt(v)}</div>
            </div>
          ))}
        </div>
      )}
      {data?.orders_summary && (
        <div data-testid="team-orders-summary" className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 flex items-center justify-between gap-2 flex-wrap text-sm">
          <span className="text-zinc-400">Total geral das OS do período (sem duplicar):</span>
          <span className="text-zinc-100"><strong data-testid="team-orders-count">{data.orders_summary.orders}</strong> OS · <strong data-testid="team-orders-revenue" className="text-green-neon">{brl(data.orders_summary.revenue)}</strong> · desconto {brl(data.orders_summary.discount)}</span>
          {tab === "all" && sumRow > ((data?.orders_summary?.revenue || 0) + 0.01) && (
            <span data-testid="team-double-count-warn" className="text-[11px] text-amber-200 w-full md:w-auto">⚠ A mesma OS entra para o mecânico e para o consultor, então a soma das linhas ({brl(sumRow)}) é maior que o total geral das OS.</span>
          )}
        </div>
      )}
      <p className="text-[11px] text-zinc-500">V.V.T. é o valor líquido (já com todos os descontos). DESC. soma o desconto por item mais o desconto total do orçamento. OS total = aprovadas ou em serviço/pronto/entregue; OS entregues = só com status Entregue. Lucro bruto = venda − custo das peças − terceiros − comissão.</p>
      {unassignedConsultants && unassignedConsultants.orders > 0 && (
        <button type="button" data-testid="team-no-consultant-warn" onClick={openUnassignedConsultants}
                className="w-full flex items-center gap-2 rounded-xl border border-amber-900/60 bg-amber-950/30 px-3 py-2 text-xs text-amber-100 hover:bg-amber-950/50">
          <AlertTriangle className="h-4 w-4 text-amber-300" />
          <span><b>{unassignedConsultants.orders}</b> OS sem consultor atribuído</span>
          <span className="ml-auto text-[11px] text-amber-200 underline">ver lista</span>
        </button>
      )}
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <>
          <Table rows={rows} sort={sort} setSort={setSort} testid={`team-${tab}`} onPrint={print} showRole={tab === "all"} expanded={expanded} setExpanded={setExpanded} summary={summary} tab={tab} consultants={data?.consultants} mode={mode} />
          <SalesRanking rows={rows} testid={`team-${tab}-ranking`} />
        </>
      )}
    </div>
  );
}
